import React, { useEffect, useState } from 'react';
import css from './ContactPatient.module.css';
import {
  ContactType,
  PatientDetails,
  VerifyPatientByMrnAndDobResult,
} from '../../../../../api/models';
import {
  ErrorBanner,
  H2,
  HelplinePhoneNumber,
  IndicatesRequired,
  LoadingSpinner,
  LoadingSpinnerSize,
  Paragraph,
  PhoneLink,
  RadioCardGroup,
  RadioCardOption,
  RadioCardSizing,
  StackedColumn,
  StackedColumnSpacing,
} from '../../../../../core';
import { ContactPatientOption } from '../ContactPatientOption';

interface ContactPatientProps {
  validate: boolean;
  patientDetails?: PatientDetails;
  contactPatientOption: ContactPatientOption;
  showError?: boolean;
  validateMrnAndDob?: () => Promise<VerifyPatientByMrnAndDobResult | undefined>;
  onChangeContactOption: (contactOption: ContactPatientOption) => void;
  onError: () => void;
}

const contactTypeOptions: RadioCardOption[] = [
  {
    value: ContactType.SMS,
    label: 'Text Message',
    description: 'Receive a text at the number on file.',
  },
  {
    value: ContactType.Voice,
    label: 'Phone Call',
    description: 'Receive a phone call at the number on file. ',
  },
];

enum ContactPatientState {
  GetOptions = 'GetOptions',
  Ready = 'Ready',
}

const errorContent = (
  <StackedColumn>
    <Paragraph text="We couldn't send the verification code." />
    <div>
      <Paragraph text="Review the information to make sure it's correct, or contact the Marshfield Clinic Health System HelpLine at: " />
      <PhoneLink phoneNumber={HelplinePhoneNumber} includeImage={false} />
    </div>
  </StackedColumn>
);

const ContactPatient: React.FC<ContactPatientProps> = (
  props: ContactPatientProps
) => {
  const [state, setState] = useState<ContactPatientState>(
    ContactPatientState.Ready
  );
  const [phoneOptions, setPhoneOptions] = useState<RadioCardOption[]>([]);
  const [contactPatientOption, setContactPatientOption] =
    useState<ContactPatientOption>({});

  useEffect(() => {
    const fillOptions = async () => {
      setState(ContactPatientState.GetOptions);
      if (props.validateMrnAndDob === undefined) {
        props.onError();
        return;
      }

      const result = await props.validateMrnAndDob();
      setState(ContactPatientState.Ready);

      if (
        result === undefined ||
        result.patientIdentityVerified !== true ||
        result.phoneNumbers === undefined
      ) {
        props.onError();
        return;
      }

      const options: RadioCardOption[] = [];

      for (const key in result.phoneNumbers) {
        options.push({ value: key, label: result.phoneNumbers[key] });
      }

      setPhoneOptions(options);
    };

    fillOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.patientDetails]);

  useEffect(() => {
    setContactPatientOption(props.contactPatientOption);
  }, [props.contactPatientOption]);

  const onChangeContactType = (selectedValue: string) => {
    const selectedResponse = selectedValue as ContactType;

    if (selectedResponse) {
      const newContactOption = {
        ...contactPatientOption,
        contactType: selectedResponse,
      };
      setContactPatientOption(newContactOption);
      props.onChangeContactOption(newContactOption);
    }
  };

  const onChangePhoneChoice = (selectedValue: string) => {
    const selectedOption = phoneOptions.find(
      (option) => option.value === selectedValue
    );
    const newContactOption = {
      ...contactPatientOption,
      phoneKey: selectedValue,
      phoneString: selectedOption?.label,
    };
    setContactPatientOption(newContactOption);
    props.onChangeContactOption(newContactOption);
  };

  return (
    <div>
      <StackedColumn fillHeight={true} spacing={StackedColumnSpacing.Wide}>
        <H2 text="To protect your account, we need to confirm it's you." />
        {state === ContactPatientState.Ready ? (
          <>
            <IndicatesRequired />
            {props.showError ? (
              <ErrorBanner title="Please try again." content={errorContent} />
            ) : null}
            <div className={css.questionContainer}>
              <StackedColumn spacing={StackedColumnSpacing.Wide}>
                <RadioCardGroup
                  label="How would you like to be contacted?"
                  optionalText="A verification code will be sent to your preferred contact method."
                  required={true}
                  validate={props.validate}
                  groupName="contactType"
                  sizing={RadioCardSizing.Auto}
                  fullWidth={true}
                  radioOptions={contactTypeOptions}
                  selectedValue={contactPatientOption.contactType ?? ''}
                  onChange={onChangeContactType}
                />
                <RadioCardGroup
                  label="Which number should we send the verification code to?"
                  required={true}
                  validate={props.validate}
                  groupName="phoneOption"
                  sizing={RadioCardSizing.Auto}
                  fullWidth={true}
                  radioOptions={phoneOptions}
                  selectedValue={contactPatientOption.phoneKey ?? ''}
                  onChange={onChangePhoneChoice}
                />
              </StackedColumn>
            </div>
          </>
        ) : (
          <LoadingSpinner
            label="Loading, please wait..."
            size={LoadingSpinnerSize.Medium}
          />
        )}
      </StackedColumn>
    </div>
  );
};

export default ContactPatient;
